import React, {
    ReactElement, useEffect, useState,
} from "react";
import {
    Button, ButtonGroup, Dropdown, Form,
} from "react-bootstrap";
import { BiConversation } from "react-icons/bi";
import { BsCheckLg } from "react-icons/bs";
import { GoNote } from "react-icons/go";
import { IoMdAttach, IoMdSend } from "react-icons/io";
import TimeAgo from "react-timeago";
import { RiCloseFill } from "react-icons/ri";
import {
    ChatStatus, TicketMessageArticleInput, useAddParticipantsMutation,
    useAssignChatsMutation, useSendMessageMutation, useSendNoteMutation,
    User,
    useSaveDraftMutation,
    TicketMessage,
} from "../../../__generated__/api";
import Attachment from "../../attachment/Attachment";
import Editor, { EditorInstance } from "../../editor/Editor";
import withRetries from "../../with-retries";
import AnswerFinder from "../answer-finder/AnswerFinder";
import ArticlePreview from "../answer-finder/ArticlePreview";
import Styles from "./text-entry.module.scss";
import Scheduler from "../../scheduler/Scheduler";
import { MentionedUser } from "../../editor/MentionBar";
import usePortal from "../../../contexts/PortalContext";
import ArtificialIntelligenceActions from "../artificial-intelligence/ArtificialIntelligenceActions";
import useLogging from "../../../contexts/logging/LoggingContext";

export interface UploadedFile {
    name: string;
    mimeType: string;
    token?: string;
    url?: string;
    error?: string;
    isUploading?: boolean;
}

interface TextEntryProps {
    id: string;
    status: ChatStatus;
    sourceId: string | undefined;
    canParticipate: boolean;
    onOpen: () => void;
    onSent: () => void;
    onRemoveAttachment: (attachments: UploadedFile[]) => void;
    attachments: UploadedFile[];
    onUploadAttachments: (file: File[]) => Promise<void>;
    className?: string;
    canAddNote?: boolean;
    canBeAssigned?: boolean;
    canAddArticles?: boolean;
    draft?: TicketMessage;
}

enum MessageType {
    PUBLIC,
    NOTE
}

const FEATURE_DRAFTS = "frontend.chat.drafts";

export default function TextEntry({
    id, status, sourceId, canParticipate, onOpen, attachments, onSent, onRemoveAttachment, className,
    canAddNote, onUploadAttachments, canBeAssigned, canAddArticles, draft,
}: TextEntryProps): ReactElement {
    const logging = useLogging();
    const [sendMessage] = useSendMessageMutation();
    const [sendNote] = useSendNoteMutation();
    const [assign] = useAssignChatsMutation();
    const [text, setText] = useState<string>(draft?.body?.html ?? "");
    const editorRef = React.createRef<EditorInstance>();
    const [type, setType] = useState<MessageType>(MessageType.PUBLIC);
    const [addParticipants] = useAddParticipantsMutation();
    const { isAIEnabled, featureFlags } = usePortal();

    const [scheduledDate, setScheduledDate] = useState<Date>();
    const [shouldShowScheduler, setShowScheduler] = useState(false);

    const [lastTimer, setTimer] = useState<ReturnType<typeof setTimeout>>();

    const [draftId, setDraftId] = useState<string>();
    const [lastDraft, setLastDraft] = useState<string>(draft?.body?.html ?? "");

    const [articles, setArticles] = useState<TicketMessageArticleInput[]>(draft?.articles ?? []);

    const [participants, setParticipants] = useState<User[]>([]);
    const [shouldAddParticipants, setShouldAddParticipants] = useState<boolean>(false);

    const [saveDraft] = useSaveDraftMutation();

    useEffect(() => {
        if (featureFlags.has(FEATURE_DRAFTS) && draft?.body.html && !text) {
            setText(draft.body.html);
            setDraftId(draft.id);
            setLastDraft(draft.body.html);
        }
    }, [draft]);

    const handleParticipantsToAdd = (user: MentionedUser): void => {
        if (!user.isInChannel) {
            setParticipants(prev => [
                ...prev,
                user.user,
            ].dedupe(agent => agent?.id));
        }
    };

    const pressed = async (html?: string) => {
        if ((!html || html.trim() === "<p></p>") && ((articles.isEmpty && attachments.isEmpty) || type !== MessageType.PUBLIC)) {
            return;
        }
        const currentArticles = articles;

        setScheduledDate(undefined);
        editorRef.current?.clear();
        editorRef.current?.focus();
        setArticles([]);
        if (canBeAssigned && (status === ChatStatus.ChatRequested || status === ChatStatus.Connected)) {
            await assign({
                // refetchQueries: [{
                //     query: GetChatDocument,
                //     variables: {
                //         id,
                //     },
                // }],
                variables: {
                    ids: [id],
                    assignment: {
                        shouldAssignSelf: true,
                    },
                },
            });
        }
        if (canParticipate || shouldAddParticipants) {
            await addParticipants({
                variables: {
                    participants: [
                        {
                            chatId: id,
                            shouldAddSelf: canParticipate,
                            participantIds: shouldAddParticipants ? participants.toIdList() : [],
                        },
                    ],
                },
            });
            setShouldAddParticipants(false);
            setParticipants([]);
        }
        if (type === MessageType.PUBLIC) {
            await withRetries(() => sendMessage({
                variables: {
                    chatId: id,
                    message: {
                        draftId,
                        body: html,
                        scheduledDate,
                        attachmentTokens: attachments.toIdList("token").filterNull(),
                        articles: currentArticles,
                    },
                },
            }));
        } else {
            await withRetries(() => sendNote({
                variables: {
                    chatId: id,
                    message: {
                        body: html as string,
                        scheduledDate,
                        attachmentTokens: attachments.toIdList("token").filterNull(),
                    },
                },
            }));
        }
        onSent();

        if (type !== MessageType.PUBLIC) {
            setType(MessageType.PUBLIC);
        }
        setParticipants([]);
    };

    const storeDraft = async () => {
        if (text === lastDraft && ((articles.isEmpty && attachments.isEmpty))) {
            return;
        }

        const savedDraft = await saveDraft({
            variables: {
                chatId: id,
                message: {
                    draftId,
                    body: text,
                    scheduledDate,
                    attachmentTokens: attachments.toIdList("token").filterNull(),
                    articles,
                },
            },
        });
        setLastDraft(text);
        if (savedDraft.data?.storeMessageDraft?.id) {
            setDraftId(savedDraft.data?.storeMessageDraft?.id);
        }
    };

    // eslint-disable-next-line arrow-body-style
    useEffect(() => {
        return () => {
            if (featureFlags.has(FEATURE_DRAFTS) && text && text !== "<p></p>" && text !== lastDraft) {
                logging.info({ draftId }, "Storing draft while unloading chat");
                storeDraft();
            }
        };
    }, []);

    useEffect(() => {
        if (featureFlags.has(FEATURE_DRAFTS)) {
            if (lastTimer) {
                clearTimeout(lastTimer);
            }
            const timer = setTimeout(storeDraft, 2000);
            setTimer(timer);
        }
    }, [text]);

    return (
        <div className={`p-2 pt-0 ${className}`}>
            <div className="w-100 justify-content-start d-flex mt-n4 ">
                <div className={`rounded d-flex bg-white ${Styles.tools}`}>
                    {canAddNote && (
                        <Dropdown>
                            <Dropdown.Toggle variant="" className="menu-small p-1 btn-none text-gray-500 pe-1">
                                {type === MessageType.PUBLIC ? <BiConversation /> : <GoNote />}
                            </Dropdown.Toggle>
                            <Dropdown.Menu>
                                <Dropdown.Item onClick={() => setType(MessageType.PUBLIC)}>
                                    <BiConversation />
                                    {" "}
                                    Public Comment
                                </Dropdown.Item>
                                <Dropdown.Item onClick={() => setType(MessageType.NOTE)}>
                                    <GoNote />
                                    {" "}
                                    Internal Note
                                </Dropdown.Item>
                            </Dropdown.Menu>
                        </Dropdown>
                    )}

                    <button type="button" className="btn btn-none p-1 text-gray-500 pe-1 " onClick={onOpen}>
                        <IoMdAttach />
                    </button>
                    {canAddArticles && type === MessageType.PUBLIC && (
                        <AnswerFinder
                            onAnswerSelected={article => setArticles([...(articles ?? []), article])}
                            onCannedMessageSelected={
                                cannedMessage => setText(previousText => previousText + cannedMessage)
                            }
                        />
                    )}
                    {isAIEnabled && type === MessageType.PUBLIC && (
                        <ArtificialIntelligenceActions
                            text={text}
                            onChange={setText}
                        />
                    )}

                </div>

            </div>
            <div className={"border-light border rounded d-flex w-100 "
                + `align-items-end ${type === MessageType.PUBLIC ? "" : "p-0 m-0 alert alert-light"}`}
            >
                <Editor
                    ref={editorRef}
                    className={`${Styles["text-container"]} p-0 pt-2 pb-2 ps-2 m-0 h-100 flex-grow-1`}
                    onCommandEnterPressed={pressed}
                    onChange={setText}
                    mentionSourceId={sourceId}
                    isScrollDisabled
                    shouldFocus
                    onFilesPasted={onUploadAttachments}
                    onUsersSelected={handleParticipantsToAdd}
                    text={text}
                />

                <Scheduler
                    date={scheduledDate}
                    shouldShow={shouldShowScheduler}
                    onCancel={() => setShowScheduler(false)}
                    onSelected={date => {
                        setShowScheduler(false);
                        setScheduledDate(date);
                    }}
                />
                <Dropdown as={ButtonGroup} drop="up">

                    <Button
                        type="button"
                        className={`btn rounded-start ${type === MessageType.PUBLIC ? "btn-primary " : "btn-secondary"}`}
                        onClick={() => pressed(text)}
                    >
                        {type === MessageType.PUBLIC ? <IoMdSend /> : <BsCheckLg />}
                        {" "}
                        {canParticipate ? "Participate and Send" : ""}
                        {(status === ChatStatus.ChatRequested || status === ChatStatus.Connected)
                            && "Take and Send"}
                        {status === ChatStatus.Finished && "Reopen and Send"}
                        {!canParticipate && status === ChatStatus.OnHold && "Unhold and Send"}
                        {status === ChatStatus.Snoozed && "Resume and Send"}
                        {status === ChatStatus.Ignored && "Unignore and Send"}

                    </Button>

                    <Dropdown.Toggle
                        split
                        variant="primary"
                        className="px-2 py-2 rounded-end border-start"
                    />

                    <Dropdown.Menu flip>
                        <Dropdown.Item onClick={() => setShowScheduler(true)}>
                            Schedule
                        </Dropdown.Item>
                    </Dropdown.Menu>
                </Dropdown>

            </div>
            {participants.isNotEmpty && (
                <div>
                    <Form.Group className="mt-1" controlId="formBasicCheckbox">
                        <Form.Check
                            type="switch"
                            label={`Add ${
                                participants.map(user => user.name).join(", ")
                            } as ${participants.length > 1 ? "participants" : "a participant"}`}
                            onChange={() => setShouldAddParticipants(prevCheck => !prevCheck)}
                        />
                    </Form.Group>
                </div>
            )}

            {scheduledDate && (
                <div className="text-primary text-end w-100 d-flex align-items-center justify-content-end">
                    <small>
                        Scheduled to send:
                        {" "}
                        <TimeAgo date={scheduledDate} title={`Sending at ${scheduledDate.toLocaleString()}`} />
                    </small>
                    <button type="button" className="btn btn-none p-0 ms-2" onClick={() => setScheduledDate(undefined)}>
                        <RiCloseFill />
                    </button>

                </div>
            )}

            {attachments?.isNotEmpty && (
                <div className="d-flex align-items-stretch flex-wrap">
                    {attachments.map(attach => (
                        <Attachment
                            size="sm"
                            className="mt-1 me-2"
                            url={attach.url as string}
                            mimeType={attach.mimeType}
                            title={attach.name}
                            key={attach.url}
                            isUploading={attach.isUploading}
                            onClose={() => onRemoveAttachment?.(attachments.filter(row => row.url !== attach.url))}
                        />
                    ))}
                </div>
            )}
            {articles?.isNotEmpty && (
                <div className="d-flex align-items-stretch flex-wrap">
                    {articles.map(article => (
                        <ArticlePreview
                            className="col-4"
                            article={article}
                            onClose={() => setArticles?.(articles.filter(row => row.id !== article.id))}
                        />
                    ))}
                </div>
            )}

        </div>
    );
}

TextEntry.defaultProps = {
    className: "",
    canAddNote: true,
    canBeAssigned: true,
    canAddArticles: false,
    draft: undefined,
};
